import s from '@/styles/components/Modal.module.scss'
import { ReactNode, useCallback, useEffect } from 'react'
import { fixScroll, unfixScroll } from '@tmap-web-lib/utils'
import { Transition } from 'framer-motion'
import { MotionDiv } from '@/components'
import { ModalBaseProps, ModalFramePhaseVariants } from '@/components/tmds/Modal/type'

interface Props extends ModalBaseProps {
  content: ReactNode;
  footer?: ReactNode;
}

/* 기본값 */
const backdropVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

const defaultVariants: Required<ModalFramePhaseVariants> = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

const defaultTransition: Transition = { duration: 0.3, ease: 'easeOut' }

function mergeVariants(base: Required<ModalFramePhaseVariants>, override?: ModalFramePhaseVariants): Required<ModalFramePhaseVariants> {
  return {
    initial: { ...base.initial, ...(override?.initial ?? {}) },
    animate: { ...base.animate, ...(override?.animate ?? {}) },
    exit: { ...base.exit, ...(override?.exit ?? {}) },
  }
}

function ModalFrame(props: Props) {
  const { content, footer, animation, contentCustomStyles, backdropRef, onContentClick } = props

  const sheetVariants = mergeVariants(defaultVariants, animation?.variants)
  const timing: Transition = { ...defaultTransition, ...(animation?.transition ?? {}) }

  const handleContent = useCallback(() => {
    onContentClick?.()
  }, [onContentClick])

  useEffect(() => {
    fixScroll('fix_scroll')
    return () => unfixScroll('fix_scroll')
  }, [])

  return (
    <div className={s.wrap}>
      <MotionDiv
        ref={backdropRef}
        className={s.backdrop}
        variants={backdropVariants}
        transition={timing}
        initial="initial"
        animate="animate"
        exit="exit"
      />
      <MotionDiv
        className={s.container}
        variants={sheetVariants}
        transition={timing}
        initial="initial"
        animate="animate"
        exit="exit"
        style={{ ...contentCustomStyles }}
      >
        <div className={s.content} onClick={handleContent}>{content}</div>
        <div className={s.footer}>{footer}</div>
      </MotionDiv>
    </div>
  )
}

export { ModalFrame }
